// Chart configuration mapper
// Maps backend graph_config to Recharts format

export const mapChartConfig = (graphConfig, domain = null) => {
  if (!graphConfig || !graphConfig.type) {
    return null;
  }

  const domainColors = {
    Finance: '#10b981',
    Time: '#f59e0b',
    Administrative: '#6366f1',
    Planet: '#06b6d4',
    Home: '#06b6d4',
    Personal: '#8b5cf6',
    Roadmap: '#9c27b0',
    Professional: '#3b82f6',
    Social: '#ec4899',
  };

  const defaultColor = domainColors[domain] || '#6366f1';

  // Handle Chart.js-style format (datasets array)
  let chartData = [];
  let colors = null;

  // Check if we have datasets array (Chart.js format)
  if (graphConfig.datasets && Array.isArray(graphConfig.datasets) && graphConfig.datasets.length > 0) {
    const dataset = graphConfig.datasets[0];
    const dataValues = dataset.data || [];
    const labels = graphConfig.labels || [];
    const backgroundColors = dataset.backgroundColor || [];

    // Ensure labels and data have the same length
    const maxLength = Math.max(labels.length, dataValues.length);

    // Transform Chart.js format to Recharts format
    chartData = Array.from({ length: maxLength }, (_, index) => ({
      name: labels[index] || `Item ${index + 1}`,
      value: dataValues[index] !== undefined ? dataValues[index] : 0,
      color: backgroundColors[index] || null,
    }));

    // Store colors for later use
    colors = backgroundColors.length > 0 ? backgroundColors : null;
  }
  // Check if we have flat format (labels + data arrays)
  else if (graphConfig.labels && graphConfig.data) {
    const labels = graphConfig.labels || [];
    const dataValues = graphConfig.data || [];
    
    // Transform flat format to Recharts format
    chartData = labels.map((label, index) => ({
      name: label,
      value: dataValues[index] !== undefined ? dataValues[index] : 0,
    }));
  }
  // Fallback to data array if it exists
  else if (graphConfig.data && Array.isArray(graphConfig.data)) {
    chartData = graphConfig.data;
  }

  const baseConfig = {
    color: graphConfig.color || defaultColor,
    data: chartData,
    labels: graphConfig.labels || [],
    title: graphConfig.title || '',
    colors: colors, // Store colors for donut/pie charts
  };

  switch (graphConfig.type.toLowerCase()) {
    case 'line':
      return {
        ...baseConfig,
        type: 'line',
        xAxisKey: graphConfig.xAxisKey || 'name',
        yAxisKey: graphConfig.yAxisKey || 'value',
        strokeWidth: graphConfig.strokeWidth || 2,
        dot: graphConfig.dot !== false,
        area: graphConfig.area || false,
      };

    case 'bar':
      return {
        ...baseConfig,
        type: 'bar',
        xAxisKey: graphConfig.xAxisKey || 'name',
        yAxisKey: graphConfig.yAxisKey || 'value',
        barSize: graphConfig.barSize || 40,
        horizontal: graphConfig.horizontal || false,
      };

    case 'donut':
    case 'doughnut':
      return {
        ...baseConfig,
        type: 'donut',
        innerRadius: graphConfig.innerRadius || 60,
        outerRadius: graphConfig.outerRadius || 80,
        labelKey: graphConfig.labelKey || 'name',
        valueKey: graphConfig.valueKey || 'value',
      };

    case 'pie':
      return {
        ...baseConfig,
        type: 'pie',
        innerRadius: 0,
        outerRadius: graphConfig.outerRadius || 80,
        labelKey: graphConfig.labelKey || 'name',
        valueKey: graphConfig.valueKey || 'value',
      };

    case 'area':
      return {
        ...baseConfig,
        type: 'area',
        xAxisKey: graphConfig.xAxisKey || 'name',
        yAxisKey: graphConfig.yAxisKey || 'value',
        strokeWidth: graphConfig.strokeWidth || 2,
        fillOpacity: graphConfig.fillOpacity || 0.6,
      };

    case 'stacked_bar':
    case 'stackbar':
      return {
        ...baseConfig,
        type: 'stacked_bar',
        xAxisKey: graphConfig.xAxisKey || 'name',
        yAxisKeys: graphConfig.yAxisKeys || ['value'],
        barSize: graphConfig.barSize || 40,
      };

    default:
      // Default to bar chart
      return {
        ...baseConfig,
        type: 'bar',
        xAxisKey: graphConfig.xAxisKey || 'name',
        yAxisKey: graphConfig.yAxisKey || 'value',
      };
  }
};

export const formatChartData = (data, config) => {
  if (!data || !Array.isArray(data)) {
    return [];
  }

  // If data is already in correct format, return as is
  if (data.every(item => item.name && item.value !== undefined)) {
    return data;
  }

  // Transform data based on config
  const xKey = config.xAxisKey || 'name';
  const yKey = config.yAxisKey || 'value';

  return data.map((item, index) => ({
    name: item[xKey] || item.name || `Item ${index + 1}`,
    value: item[yKey] !== undefined ? item[yKey] : item.value || 0,
    ...item, // Include other properties
  }));
};

export default mapChartConfig;

